/**
 * DEMO DATA — projects the simulator's completed-call ledger into exactly the
 * shape `GET /supervisor/campaign-summary` returns.
 *
 * ⚠️  PHASE 1 (CLIENT DEMO) ONLY. Real data arrives next phase. See ../demo-mode.ts.
 *
 * WHY IT MIRRORS THE API RESPONSE EXACTLY: the panels are written against
 * `CampaignSummaryResponse` and know nothing about the demo. Producing that
 * type here means demo mode is a swap at one seam rather than a fork in every
 * component — and it means the demo is a genuine rehearsal of the real
 * rendering path, so anything that looks wrong here would look wrong with real
 * data too.
 *
 * The metric FORMULAS deliberately match the SQL in
 * `apps/api/src/routes/supervisor/campaign-summary.ts` — same numerators,
 * same denominators, same rounding. If the two ever disagree, the demo stops
 * being a preview of the real thing.
 */
import type {
  AgentRow,
  CampaignRow,
  CampaignSummaryResponse,
  DispositionRow,
  IntervalRow,
  QueueRow,
  SummaryMetrics,
} from '../types.js'
import {
  DEMO_CAMPAIGNS,
  DEMO_QUEUES,
  type CompletedCall,
  type DemoState,
} from './simulator.js'

const DEFAULT_SL_TARGET_SEC = 20

function pctOf(part: number, whole: number): number {
  if (whole <= 0) return 0
  return Math.round((part / whole) * 1000) / 10
}

const sum = (xs: number[]): number => xs.reduce((a, b) => a + b, 0)

const avg = (xs: number[]): number =>
  xs.length === 0 ? 0 : Math.round(sum(xs) / xs.length)

/** Service-level target for the queue a call belongs to. */
function slTarget(queueId: string): number {
  return (
    DEMO_QUEUES.find((q) => q.queueId === queueId)?.slTargetSec ??
    DEFAULT_SL_TARGET_SEC
  )
}

/**
 * The single metric reducer. Every grouping (interval, agent, queue, campaign,
 * grand total) goes through this one function, for the same reason the API
 * defines its metric columns once: five hand-rolled copies is how "AHT" ends
 * up meaning two different things in two tables on the same screen.
 */
export function computeMetrics(calls: CompletedCall[]): SummaryMetrics {
  const answered = calls.filter((c) => c.answered)
  const abandoned = calls.filter((c) => c.abandoned)
  const totalCalls = calls.length
  const answeredTotal = answered.length

  // Abandons before reaching a queue are the IVR bucket; the simulator marks
  // queue-routed abandons with a queueId, matching the API's
  // `abandoned AND queue_id IS NULL` split.
  const abandonedIvr = abandoned.filter((c) => !c.queueId).length
  const abandonedQueue = abandoned.length - abandonedIvr

  const answeredInSl = answered.filter(
    (c) => c.waitSec <= slTarget(c.queueId),
  ).length

  return {
    totalCalls,
    totalInbound: calls.filter((c) => c.direction === 'inbound').length,
    totalOutbound: calls.filter((c) => c.direction === 'outbound').length,
    answeredTotal,
    abandonedTotal: abandoned.length,
    abandonedIvr,
    abandonedQueue,
    totalTalkSec: sum(answered.map((c) => c.talkSec)),
    avgTalkSec: avg(answered.map((c) => c.talkSec)),
    // AHT = talk + hold + wrap-up, same as the SQL.
    avgHandleSec: avg(answered.map((c) => c.talkSec + c.holdSec + c.acwSec)),
    totalHoldSec: sum(calls.map((c) => c.holdSec)),
    avgHoldSec: avg(calls.filter((c) => c.holdSec > 0).map((c) => c.holdSec)),
    totalAcwSec: sum(calls.map((c) => c.acwSec)),
    totalWaitSec: sum(answered.map((c) => c.waitSec)),
    // ASA over ANSWERED calls only — including abandons would let a queue
    // improve its ASA by losing the callers who waited longest.
    avgAnswerSpeedSec: avg(answered.map((c) => c.waitSec)),
    maxWaitSec: calls.reduce((m, c) => (c.waitSec > m ? c.waitSec : m), 0),
    answeredInSl,
    answerRatePct: pctOf(answeredTotal, totalCalls),
    abandonRatePct: pctOf(abandoned.length, totalCalls),
    ivrAbandonRatePct: pctOf(abandonedIvr, totalCalls),
    // Denominator is ANSWERED, not total — service level measures how fast the
    // calls we took were taken.
    serviceLevelPct: pctOf(answeredInSl, answeredTotal),
  }
}

function groupBy<T>(xs: T[], key: (x: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>()
  for (const x of xs) {
    const k = key(x)
    const bucket = out.get(k)
    if (bucket) bucket.push(x)
    else out.set(k, [x])
  }
  return out
}

/**
 * Build the full response for the requested window.
 *
 * `hours` and `intervalMin` come straight from the page's toolbar, so the demo
 * responds to those controls exactly as the real endpoint does — the client can
 * switch to a 60-minute bucket or a 4-hour window and watch it re-shape.
 */
export function buildDemoSummary(
  state: DemoState,
  now: number,
  hours: number,
  intervalMin: number,
  campaignId: string | null,
): CampaignSummaryResponse {
  const from = now - hours * 3600_000
  const bucketMs = intervalMin * 60_000

  let calls = state.completed.filter((c) => c.startedAt >= from)
  if (campaignId) calls = calls.filter((c) => c.campaignId === campaignId)

  /* Interval pivot — bucket boundaries computed the same way as the SQL's
     FLOOR(EPOCH / bucket) * bucket, so labels line up with the real thing. */
  const byBucket = groupBy(calls, (c) =>
    String(Math.floor(c.startedAt / bucketMs) * bucketMs),
  )
  const intervals: IntervalRow[] = Array.from(byBucket.entries())
    .map(([bucket, rows]) => ({
      bucketStart: new Date(Number(bucket)).toISOString(),
      ...computeMetrics(rows),
    }))
    .sort((a, b) => a.bucketStart.localeCompare(b.bucketStart))

  /* Per-agent */
  const nameById = new Map(state.agents.map((a) => [a.id, a]))
  const agents: AgentRow[] = Array.from(groupBy(calls, (c) => c.agentId).entries())
    .map(([agentId, rows]) => {
      const agent = nameById.get(agentId)
      return {
        agentId,
        agentName: agent?.name ?? agentId,
        agentEmail: agent?.email ?? null,
        ...computeMetrics(rows),
      }
    })
    .sort((a, b) => b.totalCalls - a.totalCalls)

  /* Per-queue */
  const queues: QueueRow[] = Array.from(groupBy(calls, (c) => c.queueId).entries())
    .map(([queueId, rows]) => ({
      queueId,
      queueName:
        DEMO_QUEUES.find((q) => q.queueId === queueId)?.queueName ?? queueId,
      slTargetSec: slTarget(queueId),
      ...computeMetrics(rows),
    }))
    .sort((a, b) => b.totalCalls - a.totalCalls)

  /* Per-campaign */
  const campaigns: CampaignRow[] = Array.from(
    groupBy(calls, (c) => c.campaignId).entries(),
  )
    .map(([cid, rows]) => {
      const demo = DEMO_CAMPAIGNS.find((c) => c.campaignId === cid)
      return {
        campaignId: cid,
        campaignName: demo?.campaignName ?? cid,
        // Demo DIDs are a fixed list per campaign; an empty array mirrors
        // a campaign with no DID configured (renders as '—'). Spread into
        // a mutable array so the readonly `as const` tuple does not leak.
        dids: demo ? [...demo.dids] : null,
        ...computeMetrics(rows),
      }
    })
    .sort((a, b) => b.totalCalls - a.totalCalls)

  /* Dispositions */
  const dispositionTotal = calls.length
  const dispositions: DispositionRow[] = Array.from(
    groupBy(calls, (c) => c.disposition).entries(),
  )
    .map(([disposition, rows]) => ({
      disposition,
      count: rows.length,
      pct: pctOf(rows.length, dispositionTotal),
    }))
    .sort((a, b) => b.count - a.count)

  return {
    serverTime: new Date(now).toISOString(),
    window: { hours, intervalMin, campaignId, queueId: null },
    intervals,
    agents,
    queues,
    campaigns,
    dispositions,
    // Computed from the whole set, NOT by summing the interval rows: averaging
    // per-bucket averages weights a quiet bucket the same as a busy one.
    totals: computeMetrics(calls),
  }
}

/** Campaign picker options for the toolbar while in demo mode. */
export const DEMO_CAMPAIGN_OPTIONS = [
  { label: 'All Campaigns', value: '' },
  ...DEMO_CAMPAIGNS.map((c) => ({ label: c.campaignName, value: c.campaignId })),
]
